import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Heart, Share2, Copy, Twitter, Facebook, QrCode, Plus, Lock, 
  ShoppingBag, FileText, Image as ImageIcon, Trash2, Edit3 
} from 'lucide-react';

import CreatorLayout from '../components/CreatorLayout';
import creatorContentService from '../services/creatorContentService';
import { config } from '../config/config';
import { useAuth } from '../context/AuthContext';
import GoldCrown from '../components/GoldCrown';

// ==========================================
// STYLED COMPONENTS (RESPONSIVE)
// ==========================================
const ProfileCard = styled.div`
  background: #111111; border: 1px solid #333; border-radius: 16px; padding: 32px; margin-bottom: 32px; box-shadow: 0 4px 20px rgba(0,0,0,0.5); display: flex; justify-content: space-between; align-items: center;
  @media (max-width: 768px) { flex-direction: column; align-items: flex-start; gap: 24px; padding: 24px 20px; }
`;
const ProfileLeft = styled.div`display: flex; align-items: center; gap: 24px;`;
const ProfileAvatar = styled.div`
  width: 80px; height: 80px; background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #000; font-weight: 600; font-size: 2rem; text-transform: uppercase;
  @media (max-width: 480px) { width: 60px; height: 60px; font-size: 1.5rem; }
`;
const ProfileInfo = styled.div`
  h2 { font-family: var(--font-heading); font-size: 1.8rem; font-weight: 700; color: #fff; margin: 0 0 8px 0; @media (max-width: 480px){font-size: 1.4rem;} } 
  p { font-family: var(--font-body); color: #aaa; margin: 0; font-size: 0.9rem; }
`;
const ProfileRight = styled.div`display: flex; flex-direction: column; gap: 24px; align-items: flex-end; @media (max-width: 768px) { align-items: flex-start; border-top: 1px solid #333; padding-top: 20px; width: 100%; }`;
const EarningsSection = styled.div`text-align: right; @media (max-width: 768px) { text-align: left; }`;
const EarningsTitle = styled.h3`font-family: var(--font-heading); font-size: 1.1rem; font-weight: 600; color: #aaa; margin: 0 0 8px 0;`;
const EarningsAmount = styled.div`font-family: var(--font-heading); font-size: 2.5rem; font-weight: 700; color: #e0c16f; margin-bottom: 16px; @media (max-width: 480px){font-size: 2rem;}`;

const SectionHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; 
  @media (max-width: 576px) { flex-direction: column; align-items: flex-start; gap: 16px; }
  h2 { color: #fff; font-family: var(--font-heading); font-size: 1.5rem; margin: 0; }
`;
const PrimaryButton = styled.button`
  background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%); color: #000; border: none; padding: 12px 24px; border-radius: 8px; font-weight: 600; cursor: pointer; transition: all 0.2s; font-family: var(--font-heading); display: flex; align-items: center; justify-content: center; gap: 8px;
  @media (max-width: 576px) { width: 100%; }
  &:hover { transform: translateY(-2px); box-shadow: 0 4px 15px rgba(224,193,111,0.3); }
`;

const ItemCard = styled.div`
  background: #111111; border: 1px solid #333; border-radius: 12px; padding: 20px; display: grid; grid-template-columns: auto 1fr auto; gap: 20px; align-items: center; transition: all 0.2s;
  @media (max-width: 576px) { grid-template-columns: 1fr; align-items: flex-start; gap: 16px; position: relative; }
  &:hover { border-color: #555; }
`;
const ItemImage = styled.div`
  width: 80px; height: 80px; background: #222; border-radius: 8px; overflow: hidden; display: flex; align-items: center; justify-content: center; color: #666; 
  @media (max-width: 576px) { width: 100%; height: 150px; }
  img { width: 100%; height: 100%; object-fit: cover; }
`;
const ItemInfo = styled.div`
  h3 { color: #fff; margin: 0 0 8px 0; font-size: 1.1rem; } 
  p { color: #888; margin: 0; font-size: 0.9rem; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
`;
const ActionGroup = styled.div`
  display: flex; gap: 8px; 
  @media (max-width: 576px) { position: absolute; top: 16px; right: 16px; background: rgba(0,0,0,0.7); border-radius: 8px; padding: 4px; }
  button { background: transparent; padding: 8px; border-radius: 6px; cursor: pointer; transition: all 0.2s; border: 1px solid #333; display: flex; align-items: center; justify-content: center; } 
  .delete { color: #ff4757; &:hover { border-color: #ff4757; background: rgba(255,71,87,0.1); } }
`;

const ModalOverlay = styled.div`position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.85); backdrop-filter: blur(5px); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; box-sizing: border-box;`;
const ModalContent = styled.div`background: #111111; border: 1px solid #333; border-radius: 16px; padding: 32px; width: 100%; max-width: 500px; box-shadow: 0 10px 40px rgba(0,0,0,0.8); max-height: 90vh; overflow-y: auto; @media (max-width: 480px){ padding: 24px 16px; }`;
const ModalTitle = styled.h3`color: #fff; font-family: var(--font-heading); font-size: 1.5rem; margin: 0 0 24px 0; border-bottom: 1px solid #333; padding-bottom: 16px;`;
const FormGroup = styled.div`margin-bottom: 20px; label { display: block; color: #aaa; margin-bottom: 8px; font-size: 0.9rem; }`;
const ModalInput = styled.input`width: 100%; box-sizing: border-box; padding: 12px 16px; background: #000; border: 1px solid #333; border-radius: 8px; color: #fff; font-family: var(--font-body); &:focus { outline: none; border-color: #e0c16f; }`;
const ModalTextarea = styled.textarea`width: 100%; box-sizing: border-box; padding: 12px 16px; background: #000; border: 1px solid #333; border-radius: 8px; color: #fff; font-family: var(--font-body); resize: vertical; min-height: 100px; &:focus { outline: none; border-color: #e0c16f; }`;
const ModalSelect = styled.select`width: 100%; box-sizing: border-box; padding: 12px 16px; background: #000; border: 1px solid #333; border-radius: 8px; color: #fff; cursor: pointer; &:focus { outline: none; border-color: #e0c16f; }`;
const ModalButtons = styled.div`display: flex; gap: 12px; margin-top: 32px; @media (max-width: 480px){ flex-direction: column-reverse; } button { flex: 1; padding: 12px; border-radius: 8px; font-weight: 600; cursor: pointer; font-family: var(--font-heading); transition: all 0.2s; } .cancel { background: transparent; color: #fff; border: 1px solid #555; &:hover { background: #222; } } .save { background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%); color: #000; border: none; &:hover { transform: translateY(-2px); } }`;

// ==========================================
// SUB-COMPONENTS
// ==========================================

const GalleryManagement = ({ items, onAdd, onDelete }) => {
  const [showModal, setShowModal] = useState(false);
  const [data, setData] = useState({ title: '', description: '', visibility: 'public', allowDownload: 'false', file: null });

  const handleSave = () => {
    if (!data.title || !data.file) return alert('Title and Image are required!');
    onAdd(data);
    setShowModal(false);
  };

  return (
    <div>
      <SectionHeader>
        <h2>Gallery</h2>
        <PrimaryButton onClick={() => { setData({ title: '', description: '', visibility: 'public', allowDownload: 'false', file: null }); setShowModal(true); }}>
          <Plus size={18} /> Add Image
        </PrimaryButton>
      </SectionHeader>
      
      {items.length === 0 ? <p style={{color: '#666'}}>No images in your gallery yet.</p> : (
        <div style={{display: 'grid', gap: '16px'}}>
          {items.map(item => (
            <ItemCard key={item.id}>
              <ItemImage><img src={creatorContentService.getImageUrl(item.image_url)} alt={item.title} /></ItemImage>
              <ItemInfo><h3>{item.title}</h3><p>{item.description}</p></ItemInfo>
              <ActionGroup>
                <button className="delete" onClick={() => onDelete(item.id)}><Trash2 size={18} /></button>
              </ActionGroup>
            </ItemCard>
          ))}
        </div>
      )}

      {showModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Add Gallery Image</ModalTitle>
            <FormGroup><label>Select Image</label><ModalInput type="file" accept="image/*" onChange={(e) => setData({...data, file: e.target.files[0]})} /></FormGroup>
            <FormGroup><label>Title</label><ModalInput type="text" placeholder="E.g. Behind the scenes" value={data.title} onChange={e => setData({...data, title: e.target.value})} /></FormGroup>
            <FormGroup><label>Description</label><ModalTextarea placeholder="Describe this image..." value={data.description} onChange={e => setData({...data, description: e.target.value})} /></FormGroup>
            <FormGroup>
              <label>Visibility</label>
              <ModalSelect value={data.visibility} onChange={e => setData({...data, visibility: e.target.value})}>
                <option value="public">Public (Everyone)</option>
                <option value="supporters_only">Supporters Only</option>
              </ModalSelect>
            </FormGroup>
            <ModalButtons><button className="cancel" onClick={() => setShowModal(false)}>Cancel</button><button className="save" onClick={handleSave}>Upload</button></ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </div>
  );
};

const FeedManagement = ({ posts, onAdd, onDelete }) => {
  const [showModal, setShowModal] = useState(false);
  const [data, setData] = useState({ title: '', content: '', visibility: 'public', file: null });

  const handleSave = () => {
    if (!data.title || !data.content) return alert('Title and Content are required!');
    onAdd(data);
    setShowModal(false);
  };

  return (
    <div>
      <SectionHeader>
        <h2>Feed Posts</h2>
        <PrimaryButton onClick={() => { setData({ title: '', content: '', visibility: 'public', file: null }); setShowModal(true); }}>
          <Plus size={18} /> New Post
        </PrimaryButton>
      </SectionHeader>
      
      {posts.length === 0 ? <p style={{color: '#666'}}>You haven't published any posts yet.</p> : (
        <div style={{display: 'grid', gap: '16px'}}>
          {posts.map(post => (
            <ItemCard key={post.id}>
              <ItemImage>{post.image_url ? <img src={creatorContentService.getImageUrl(post.image_url)} alt="post" /> : <FileText size={32}/>}</ItemImage>
              <ItemInfo><h3>{post.title}</h3><p>{post.content}</p></ItemInfo>
              <ActionGroup><button className="delete" onClick={() => onDelete(post.id)}><Trash2 size={18} /></button></ActionGroup>
            </ItemCard>
          ))}
        </div>
      )}

      {showModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Create New Post</ModalTitle>
            <FormGroup><label>Post Title</label><ModalInput type="text" placeholder="What's on your mind?" value={data.title} onChange={e => setData({...data, title: e.target.value})} /></FormGroup>
            <FormGroup><label>Content</label><ModalTextarea placeholder="Write your post here..." value={data.content} onChange={e => setData({...data, content: e.target.value})} /></FormGroup>
            <FormGroup><label>Attach Image (Optional)</label><ModalInput type="file" accept="image/*" onChange={(e) => setData({...data, file: e.target.files[0]})} /></FormGroup>
            <FormGroup>
              <label>Who can see this?</label>
              <ModalSelect value={data.visibility} onChange={e => setData({...data, visibility: e.target.value})}>
                <option value="public">Public (Everyone)</option>
                <option value="supporters_only">Supporters Only</option>
              </ModalSelect>
            </FormGroup>
            <ModalButtons><button className="cancel" onClick={() => setShowModal(false)}>Cancel</button><button className="save" onClick={handleSave}>Publish</button></ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </div>
  );
};

const ShopManagement = ({ items, onAdd, onDelete }) => {
  const [showModal, setShowModal] = useState(false);
  const [data, setData] = useState({ name: '', price: '', description: '', type: 'digital', file: null });

  const handleSave = () => {
    if (!data.name || !data.price || !data.description || !data.file) return alert('All fields including image are required!');
    onAdd(data);
    setShowModal(false);
  };

  return (
    <div>
      <SectionHeader>
        <h2>Shop Products</h2>
        <PrimaryButton onClick={() => { setData({ name: '', price: '', description: '', type: 'digital', file: null }); setShowModal(true); }}>
          <Plus size={18} /> Add Product
        </PrimaryButton>
      </SectionHeader>
      
      {items.length === 0 ? <p style={{color: '#666'}}>Your shop is empty.</p> : (
        <div style={{display: 'grid', gap: '16px'}}>
          {items.map(item => (
            <ItemCard key={item.id}>
              <ItemImage><img src={creatorContentService.getImageUrl(item.image_url)} alt={item.name} /></ItemImage>
              <ItemInfo>
                <div style={{display:'flex', gap:'12px', alignItems:'center', flexWrap: 'wrap'}}>
                  <h3 style={{margin:0}}>{item.name}</h3>
                  <span style={{color: '#e0c16f', fontWeight: 'bold'}}>${item.price}</span>
                </div>
                <p style={{marginTop:'8px'}}>{item.description}</p>
              </ItemInfo>
              <ActionGroup><button className="delete" onClick={() => onDelete(item.id)}><Trash2 size={18} /></button></ActionGroup>
            </ItemCard>
          ))}
        </div>
      )}

      {showModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Add Product</ModalTitle>
            <FormGroup><label>Product Image</label><ModalInput type="file" accept="image/*" onChange={(e) => setData({...data, file: e.target.files[0]})} /></FormGroup>
            <FormGroup><label>Product Name</label><ModalInput type="text" value={data.name} onChange={e => setData({...data, name: e.target.value})} /></FormGroup>
            <div style={{display: 'flex', gap: '16px', flexWrap: 'wrap'}}>
              <FormGroup style={{flex: 1, minWidth: '100px'}}><label>Price ($)</label><ModalInput type="number" step="0.01" value={data.price} onChange={e => setData({...data, price: e.target.value})} /></FormGroup>
              <FormGroup style={{flex: 1, minWidth: '150px'}}><label>Type</label><ModalSelect value={data.type} onChange={e => setData({...data, type: e.target.value})}><option value="digital">Digital</option><option value="physical">Physical</option></ModalSelect></FormGroup>
            </div>
            <FormGroup><label>Description</label><ModalTextarea value={data.description} onChange={e => setData({...data, description: e.target.value})} /></FormGroup>
            <ModalButtons><button className="cancel" onClick={() => setShowModal(false)}>Cancel</button><button className="save" onClick={handleSave}>Add</button></ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </div>
  );
};

const MembershipsManagement = () => {
  const [tiers, setTiers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ name: '', price: '', description: '', features: '' });

  useEffect(() => { loadTiers(); }, []);

  const loadTiers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.API_BASE_URL}/creator-content/creator-status`, { headers: { 'Authorization': `Bearer ${token}` } });
      const statusData = await res.json();
      if (statusData.isCreator) {
        const tiersRes = await fetch(`${config.API_BASE_URL}/subscriptions/plans/my`, { headers: { 'Authorization': `Bearer ${token}` } });
        const tiersData = await tiersRes.json();
        setTiers(tiersData.plans || []);
      }
    } catch (error) { console.error(error); } finally { setLoading(false); }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${config.API_BASE_URL}/subscriptions/plans`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({name:data.name, price:Number(data.price), description:data.description, billing_cycle:'monthly', benefits:data.features.split(',').map(v=>v.trim()).filter(Boolean)}) });
    if (!response.ok) { const result = await response.json(); return alert(result.error || result.errors?.[0]?.msg || 'Unable to create membership'); }
    setShowModal(false); loadTiers();
  };

  const handleDelete = async (id) => {
    if(window.confirm('Delete tier?')) {
      const token = localStorage.getItem('token');
      await fetch(`${config.API_BASE_URL}/subscriptions/plans/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      loadTiers();
    }
  };

  return (
    <div>
      <SectionHeader><h2>Membership Tiers</h2><PrimaryButton onClick={() => { setData({ name: '', price: '', description: '', features: '' }); setShowModal(true); }}><Plus size={18} /> Create Tier</PrimaryButton></SectionHeader>
      {loading ? <p>Loading...</p> : tiers.length === 0 ? <p>No tiers set up.</p> : (
        <div style={{display: 'grid', gap: '16px'}}>{tiers.map(tier => (
            <ItemCard key={tier.id}>
              <div style={{width: '60px', height: '60px', background: '#222', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#e0c16f'}}><Lock size={24} /></div>
              <ItemInfo><div style={{display:'flex', gap:'12px', alignItems:'center'}}><h3 style={{margin:0}}>{tier.name}</h3><span style={{color: '#e0c16f', fontWeight: 'bold'}}>${tier.price}/mo</span></div><p style={{marginTop:'8px'}}>{tier.description}</p></ItemInfo>
              <ActionGroup><button className="delete" onClick={() => handleDelete(tier.id)}><Trash2 size={18} /></button></ActionGroup>
            </ItemCard>
          ))}</div>
      )}

      {showModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Create Tier</ModalTitle>
            <FormGroup><label>Name</label><ModalInput type="text" value={data.name} onChange={e => setData({...data, name: e.target.value})} /></FormGroup>
            <FormGroup><label>Price</label><ModalInput type="number" step="0.01" value={data.price} onChange={e => setData({...data, price: e.target.value})} /></FormGroup>
            <FormGroup><label>Description</label><ModalTextarea value={data.description} onChange={e => setData({...data, description: e.target.value})} /></FormGroup>
            <FormGroup><label>Features (comma separated)</label><ModalInput type="text" value={data.features} onChange={e => setData({...data, features: e.target.value})} /></FormGroup>
            <ModalButtons><button className="cancel" onClick={() => setShowModal(false)}>Cancel</button><button className="save" onClick={handleSave}>Save</button></ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </div>
  );
};

// ===============================================
// MAIN DASHBOARD COMPONENT
// ===============================================
const CreatorDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth(); 
  
  const [activeSection, setActiveSection] = useState('dashboard');
  const [openNavItem, setOpenNavItem] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [galleryItems, setGalleryItems] = useState([]);
  const [feedPosts, setFeedPosts] = useState([]);
  const [shopItems, setShopItems] = useState([]);
  
  const creatorIdRef = useRef(null);
  const [stats, setStats] = useState({ total_earned: 0 });

  useEffect(() => { if(user) checkCreatorStatus(); }, [user]);

  const checkCreatorStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_BASE_URL}/creator-content/creator-status`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) {
        const data = await response.json();
        if (data.isCreator) {
          creatorIdRef.current = data.creatorId; 
          loadCreatorContent(data.creatorId);
        } else {
          const setupRes = await fetch(`${config.API_BASE_URL}/creator-content/setup-creator`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
          const setupData = await setupRes.json();
          creatorIdRef.current = setupData.creatorId; 
          loadCreatorContent(setupData.creatorId);
        }
      }
    } catch (error) { console.error(error); } finally { setLoading(false); }
  };

  const loadCreatorContent = async (id) => {
    if (!id || id === 'null') return; 
    try {
      setLoading(true);
      const [galleryData, feedData, shopData, statsRes] = await Promise.all([
        creatorContentService.getGalleryItems(id),
        creatorContentService.getFeedPosts(id),
        creatorContentService.getShopItems(id),
        fetch(`${config.API_BASE_URL}/creators/${id}/stats`).then(r => r.ok ? r.json() : {})
      ]);
      setGalleryItems(galleryData || []); setFeedPosts(feedData || []); setShopItems(shopData || []);
      if(statsRes && statsRes.total_earned) setStats(statsRes);
    } catch (error) { console.error(error); } finally { setLoading(false); }
  };

  const handleAddGallery = async (d) => {
    const fd = new FormData(); fd.append('title', d.title); fd.append('description', d.description); fd.append('visibility', d.visibility); fd.append('image', d.file);
    await fetch(`${config.API_BASE_URL}/creator-content/gallery`, { method: 'POST', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }, body: fd });
    loadCreatorContent(creatorIdRef.current);
  };

  const handleDeleteGallery = async (id) => {
    if(window.confirm('Are you sure?')) {
      await fetch(`${config.API_BASE_URL}/creator-content/gallery/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }); // ISPRAVLJENO: ovde je ranije bio 'token' varijabla koja nije postojala lokalno
      loadCreatorContent(creatorIdRef.current);
    }
  };

  const handleAddPost = async (d) => {
    const fd = new FormData(); fd.append('title', d.title); fd.append('content', d.content); fd.append('visibility', d.visibility); if(d.file) fd.append('image', d.file);
    await fetch(`${config.API_BASE_URL}/creator-content/posts`, { method: 'POST', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }, body: fd });
    loadCreatorContent(creatorIdRef.current);
  };

  const handleDeletePost = async (id) => {
    if(window.confirm('Delete?')) {
      await fetch(`${config.API_BASE_URL}/creator-content/posts/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      loadCreatorContent(creatorIdRef.current);
    }
  };

  const handleAddShop = async (d) => {
    const fd = new FormData(); fd.append('name', d.name); fd.append('price', d.price); fd.append('description', d.description); fd.append('type', d.type); fd.append('image', d.file);
    await fetch(`${config.API_BASE_URL}/creator-content/shop`, { method: 'POST', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }, body: fd });
    loadCreatorContent(creatorIdRef.current);
  };

  const handleDeleteShop = async (id) => {
    if(window.confirm('Delete?')) {
      await fetch(`${config.API_BASE_URL}/creator-content/shop/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      loadCreatorContent(creatorIdRef.current);
    }
  };

  const handleNavItemClick = (section) => {
    if (section === 'supporters') navigate('/creator/supporters');
    else if (section === 'settings') navigate('/creator/settings');
    else if (section === 'view-page') window.open(`/${user.username}`, '_blank');
    else if (section === 'explore-creators') navigate('/creators');
    else setActiveSection(section);
  };

  const startGoldCheckout = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/payments/platform-gold`, {method:'POST',headers:{Authorization:`Bearer ${localStorage.getItem('token')}`}});
      const data = await response.json();
      if (!response.ok || !data.url) throw new Error(data.error || 'Unable to start checkout');
      window.location.href = data.url;
    } catch (error) { alert(error.message); }
  };

  if (!user) return null;
  const displayName = user.display_name || user.username;

  const renderContent = () => {
    switch (activeSection) {
      case 'gallery': return <GalleryManagement items={galleryItems} onAdd={handleAddGallery} onDelete={handleDeleteGallery} />;
      case 'feed': return <FeedManagement posts={feedPosts} onAdd={handleAddPost} onDelete={handleDeletePost} />;
      case 'shop-management': return <ShopManagement items={shopItems} onAdd={handleAddShop} onDelete={handleDeleteShop} />;
      case 'memberships': return <MembershipsManagement />;
      default:
        return (
          <>
            <ProfileCard>
              <ProfileLeft><ProfileAvatar>{displayName.charAt(0)}</ProfileAvatar><ProfileInfo><h2>{displayName}</h2><p>@{user.username}</p></ProfileInfo></ProfileLeft>
              <ProfileRight><EarningsSection><EarningsTitle>Total Earnings</EarningsTitle><EarningsAmount>${Number(stats.total_earned || 0).toFixed(2)}</EarningsAmount></EarningsSection></ProfileRight>
            </ProfileCard>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px', marginTop: '40px'}}>
               <div style={{background: '#111', border: '1px solid #333', padding: '24px', borderRadius: '12px', textAlign: 'center'}}><Lock size={32} color="#e0c16f" style={{marginBottom:'16px'}}/><h4>Memberships</h4><p style={{color:'#888', fontSize:'0.9rem', marginBottom:'16px'}}>Earn monthly income.</p><button onClick={() => setActiveSection('memberships')} style={{background:'transparent', color:'#e0c16f', border:'1px solid #e0c16f', padding:'8px 16px', borderRadius:'6px', cursor:'pointer', width: '100%'}}>Manage</button></div>
               <div style={{background: '#111', border: '1px solid #333', padding: '24px', borderRadius: '12px', textAlign: 'center'}}><ShoppingBag size={32} color="#e0c16f" style={{marginBottom:'16px'}}/><h4>Shop</h4><p style={{color:'#888', fontSize:'0.9rem', marginBottom:'16px'}}>Sell goods.</p><button onClick={() => setActiveSection('shop-management')} style={{background:'transparent', color:'#e0c16f', border:'1px solid #e0c16f', padding:'8px 16px', borderRadius:'6px', cursor:'pointer', width: '100%'}}>Products</button></div>
               <div style={{background: '#111', border: '1px solid #333', padding: '24px', borderRadius: '12px', textAlign: 'center'}}><div style={{display:'flex',justifyContent:'center',marginBottom:'12px'}}><GoldCrown /></div><h4>BUCX Gold</h4><p style={{color:'#888', fontSize:'0.9rem', marginBottom:'16px'}}>Add the gold crown and remove the platform fee for $25/month.</p><button onClick={startGoldCheckout} style={{background:'linear-gradient(95deg,#e0c16f,#bd851b)', color:'#000', border:0, padding:'9px 16px', borderRadius:'6px', cursor:'pointer', fontWeight:800, width:'100%'}}>Upgrade to Gold</button></div>
            </div>
          </>
        );
    }
  };

  return (
    <CreatorLayout user={user} activeSection={activeSection} onNavItemClick={handleNavItemClick} openNavItem={openNavItem} setOpenNavItem={setOpenNavItem}>
      {loading ? <div style={{color: '#fff'}}>Loading content...</div> : renderContent()}
    </CreatorLayout>
  );
};

export default CreatorDashboard;
